import React, { useEffect, useState } from 'react';
import { useOidc, useOidcUser } from '@axa-fr/react-oidc';
import { OpenIdContext, IOpenIdContext } from './OpenIdContext';
import { withOIDCProvider } from './withOIDCProvider';
import { useClaims } from './lib';
import { AppLoader } from '@shared/UI';
import { User } from '@shared/api';
import { getByUsername } from './api';

const OpenIdProviderContent: React.FC<React.PropsWithChildren> = (props) => {
    const { children } = props;

    const { logout } = useOidc();
    const { oidcUser } = useOidcUser();

    const { permissions, features, token, roles } = useClaims();

    const [isLoading, setIsLoading] = useState(true); // Track loading state

    const value: IOpenIdContext = {
        logout,
        user: oidcUser as User,
        permissions,
        features,
        token,
        roles,
    };

    // Fetch user data and store it in localStorage
    useEffect(() => {
        const userStorage = localStorage.getItem('User');

        const fetchAndStoreUserData = async () => {
            if (value.user?.email) {
                const userObject = userStorage ? JSON.parse(userStorage) : null;

                // If email doesn't match, remove old user data and fetch new one
                if (userObject && userObject.email !== value.user.email) {
                    localStorage.removeItem('User');
                    const user = await getByUsername(value.user?.email);
                    localStorage.setItem('User', JSON.stringify(user));
                } else if (!userObject) {
                    // If no user data exists, fetch and store it
                    const user = await getByUsername(value.user?.email);
                    localStorage.setItem('User', JSON.stringify(user));
                }
                setIsLoading(false); // Data fetched and stored, set loading to false
            }
        };

        // Only fetch data if there's a valid token and user email
        if (token && value.user?.email) {
            fetchAndStoreUserData();
        }
    }, [value.user?.email, token]); // Re-run if token or email changes

    // Show loading spinner until data is fetched and token is present
    if (isLoading || !token) {
        return <AppLoader />;
    }

    return <OpenIdContext.Provider value={value}>{children}</OpenIdContext.Provider>;
};

export const OpenIdProvider = withOIDCProvider(OpenIdProviderContent);
